import { StaticContext } from '@sm/webassets';
import React, { createContext, useMemo, useContext, useCallback, useState } from 'react';
import { PackageIds, SkuTypes } from '~shared/constants/pricing';
import { PricingPackage } from '~shared/types/pricing/pricingsvc';
import { PackageSkuCost, PackagesProviderProps, PackagesHook, PackageMap } from '~shared/types/pricing/package';
import { usePackageData } from './usePackageData';

const NULL_PACKAGE = {} as PricingPackage;
const ANONYMOUS_PACKAGE = 1;

// NOOP API so the page doesn't explode on initial render
export const PackagesContext = createContext<PackagesHook>({
  allPackages: [],
  userPackage: NULL_PACKAGE,
  displayPackages: [],
  showEduDiscount: false,
  packagesToDisplay: [],
  comparePlans: false,
  overageCost: undefined,
  getPackageById: packageId => NULL_PACKAGE,
});

export function PackagesProvider({
  packagesToDisplay,
  showEduDiscount,
  comparePlans,
  children,
}: PackagesProviderProps): JSX.Element {
  const packages = usePackageData();
  const [packagesForExperience, setPackagesForExperience] = useState<PricingPackage[]>(packages);
  const [packageIdToPackageMap, setPackageIdToPackageMap] = useState<PackageMap>({});

  const { user } = useContext(StaticContext);

  // this will reformat the data from pricingsvc to match whether we are displaying
  // the educational discount or not. since the OG data contains _all_ the information,
  // we just need to fake what gets disseminated to the page components at this point
  useMemo(() => {
    const updatedPackageMap: PackageMap = {};
    const updatedPackages = packages.map(pkg => {
      const newPackage = {
        ...pkg,

        // `comparisonSkuCosts` is the OG pricing data, so if this isn't EDU pricing,
        // we'll slip that in as the normal `skuCost`
        skuCost: showEduDiscount || !pkg.comparisonSkuCost ? pkg.skuCost : pkg.comparisonSkuCost,

        // in normal view, don't send the comparison costs down
        comparisonSkuCost: showEduDiscount ? pkg.comparisonSkuCost : undefined,
      };
      updatedPackageMap[parseInt(newPackage.id, 10)] = newPackage;
      return newPackage;
    });

    setPackagesForExperience(updatedPackages);
    setPackageIdToPackageMap(updatedPackageMap);
  }, [showEduDiscount, packages, setPackagesForExperience, setPackageIdToPackageMap]);

  const getPackageById = useCallback(
    packageId => packageIdToPackageMap[parseInt(packageId, 10)],
    [packageIdToPackageMap]
  );

  const userPackage = useMemo<PricingPackage>(
    () => getPackageById(user.package || ANONYMOUS_PACKAGE),
    [user, getPackageById]
  );

  const overageCost = useMemo<PackageSkuCost | undefined>(() => {
    // From the implementation of BILLING-18548, overage cost is package agnostic, so we'll
    // pull the overage SKU cost from team premier
    const teamPremier = getPackageById(PackageIds.TEAM_PREMIER_ANNUAL);
    return teamPremier?.skuCost.find(skuCost => skuCost.skuType === SkuTypes.ResponseOverage);
  }, [getPackageById]);

  const displayPackages = useMemo(
    () => packagesToDisplay.map(packageName => packageIdToPackageMap[PackageIds[packageName]]),
    [packagesToDisplay, packageIdToPackageMap]
  );

  const hookApi = useMemo(
    () => ({
      allPackages: packagesForExperience,
      displayPackages,
      userPackage,
      showEduDiscount,
      packagesToDisplay,
      comparePlans,
      overageCost,
      getPackageById,
    }),
    [
      packagesForExperience,
      displayPackages,
      userPackage,
      showEduDiscount,
      packagesToDisplay,
      comparePlans,
      overageCost,
      getPackageById,
    ]
  );

  return <PackagesContext.Provider value={hookApi}>{children}</PackagesContext.Provider>;
}

export const usePackages = (): PackagesHook => useContext(PackagesContext);
